import {
  AutotractorPropsType,
  Domain,
  InteractionLayer,
  LayerConfig,
} from "common/components/map/layers/types";
import {
  CenterPivotFeature,
  CenterPivotFeatureProperties,
  getCenterPivotFeature,
} from "common/utils/farm";
import { destinationAlongLine } from "common/utils/geo";
import { DeviceLocationHistoryRecord } from "protos/rtc/device_location_history";
import { Feature, FeatureCollection, Geometry, Point } from "geojson";
import { Layer, Source } from "react-map-gl";
import { useTheme } from "@mui/material";
import { Zone } from "protos/farm/farm";
import buffer from "@turf/buffer";
import React, { FunctionComponent, useMemo } from "react";

interface MultiPivotLayersProps {
  zones: Zone[];
  liveData: {
    [k: string]: {
      liveDeviceData: DeviceLocationHistoryRecord | undefined;
      deviceIsMoving: boolean | undefined;
      deviceIsStale: boolean | undefined;
    };
  };
  focusedZoneIds?: string[];
  selectedZoneIds?: string[];
  beforeId?: string;
}

enum PivotLayerIds {
  CENTER = "center",
  CENTER_FOCUS = "center-focus",
  CENTER_SELECT = "center-select",
  ENDPOINT = "endpoint",
  LEG = "leg",
  LEG_OUTLINE = "leg-outline",
  LEG_FOCUS = "leg-focus",
  LEG_SELECT = "leg-select",
}

const _MultiPivotLayers: FunctionComponent<MultiPivotLayersProps> = ({
  zones,
  liveData,
  focusedZoneIds,
  selectedZoneIds,
  beforeId,
}) => {
  const theme = useTheme();
  const {
    centerPointFeatures,
    endPointFeatures,
    lineFeatures,
    selectedLineFeatures,
    focusedLineFeatures,
    focusedCenterFeatures,
    selectedCenterFeatures,
  } = useMemo(() => {
    const centerPointFeatures: FeatureCollection<
      Point,
      CenterPivotFeatureProperties
    > = {
      type: "FeatureCollection",
      features: [],
    };
    const focusedCenterFeatures: FeatureCollection<
      Point,
      CenterPivotFeatureProperties
    > = {
      type: "FeatureCollection",
      features: [],
    };
    const selectedCenterFeatures: FeatureCollection<
      Point,
      CenterPivotFeatureProperties
    > = {
      type: "FeatureCollection",
      features: [],
    };
    const endPointFeatures: FeatureCollection<
      Point,
      CenterPivotFeatureProperties
    > = {
      type: "FeatureCollection",
      features: [],
    };
    const lineFeatures: FeatureCollection<
      Geometry,
      CenterPivotFeatureProperties
    > = {
      type: "FeatureCollection",
      features: [],
    };
    const selectedLineFeatures: FeatureCollection<
      Geometry,
      CenterPivotFeatureProperties
    > = {
      type: "FeatureCollection",
      features: [],
    };
    const focusedLineFeatures: FeatureCollection<
      Geometry,
      CenterPivotFeatureProperties
    > = {
      type: "FeatureCollection",
      features: [],
    };
    for (const zone of zones) {
      const pivot = zone.contents?.centerPivot;
      if (!pivot) {
        continue;
      }
      if (!pivot.center) {
        continue;
      }
      const centerCoords = [pivot.center.lng, pivot.center.lat];

      const f: Feature<Point, CenterPivotFeatureProperties> = {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: centerCoords,
        },
        properties: {
          type: AutotractorPropsType.PIVOT,
          zoneId: zone.id?.id,
          data: pivot,
          domain: Domain.AUTOTRACTOR,
          __portalFeatureProps: true,
        },
      };
      // center dot
      centerPointFeatures.features.push(f);
      if (
        zone.id?.id &&
        focusedZoneIds &&
        focusedZoneIds.includes(zone.id.id)
      ) {
        focusedCenterFeatures.features.push(f);
      }
      if (
        zone.id?.id &&
        selectedZoneIds &&
        selectedZoneIds.includes(zone.id.id)
      ) {
        selectedCenterFeatures.features.push(f);
      }

      for (const leg of pivot.legs) {
        const liveEndpoint =
          liveData[leg.endpointDeviceId]?.liveDeviceData?.point;
        if (!liveEndpoint) {
          continue;
        }
        const endPointCoord = [liveEndpoint.lng, liveEndpoint.lat];
        // line
        const lineCoords = destinationAlongLine(
          centerCoords,
          endPointCoord,
          leg.lengthMeters
        );
        const lineFeature: Feature = {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: lineCoords,
          },
          properties: {},
        };
        const bufferedLineFeature = buffer(lineFeature, leg.widthMeters, {
          units: "meters",
        });
        const f: Feature<Geometry, CenterPivotFeatureProperties> = {
          ...bufferedLineFeature,
          properties: {
            type: AutotractorPropsType.PIVOT,
            zoneId: zone.id?.id,
            data: pivot,
            domain: Domain.AUTOTRACTOR,
            __portalFeatureProps: true,
          },
        };
        lineFeatures.features.push(f);
        if (
          zone.id?.id &&
          selectedZoneIds &&
          selectedZoneIds.includes(zone.id.id)
        ) {
          selectedLineFeatures.features.push(f);
        }
        if (
          zone.id?.id &&
          focusedZoneIds &&
          focusedZoneIds.includes(zone.id.id)
        ) {
          focusedLineFeatures.features.push(f);
        }
        // endpoint
        endPointFeatures.features.push({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: endPointCoord,
          },
          properties: {
            type: AutotractorPropsType.PIVOT,
            zoneId: zone.id?.id,
            data: pivot,
            domain: Domain.AUTOTRACTOR,
            __portalFeatureProps: true,
          },
        });
      }
    }

    return {
      centerPointFeatures,
      endPointFeatures,
      lineFeatures,
      selectedLineFeatures,
      focusedLineFeatures,
      focusedCenterFeatures,
      selectedCenterFeatures,
    };
  }, [zones, liveData, selectedZoneIds, focusedZoneIds]);

  return (
    <>
      <Source type="geojson" data={centerPointFeatures}>
        <Layer
          id={PivotLayerIds.CENTER}
          beforeId={beforeId}
          type="circle"
          paint={{ "circle-color": theme.palette.common.black }}
        />
      </Source>
      <Source type="geojson" data={focusedCenterFeatures}>
        <Layer
          id={PivotLayerIds.CENTER_SELECT}
          beforeId={beforeId}
          type="circle"
          paint={{
            "circle-color": theme.palette.common.white,
            "circle-opacity": 0.9,
          }}
        />
      </Source>
      <Source type="geojson" data={selectedCenterFeatures}>
        <Layer
          id={PivotLayerIds.CENTER_FOCUS}
          beforeId={beforeId}
          type="circle"
          paint={{
            "circle-color": theme.palette.common.white,
            "circle-opacity": 0.7,
          }}
        />
      </Source>
      <Source type="geojson" data={endPointFeatures}>
        <Layer
          id={PivotLayerIds.ENDPOINT}
          beforeId={PivotLayerIds.CENTER}
          type="circle"
          paint={{ "circle-color": theme.palette.common.black }}
        />
      </Source>
      <Source type="geojson" data={lineFeatures}>
        <Layer
          id={PivotLayerIds.LEG}
          beforeId={PivotLayerIds.CENTER}
          type="fill"
          paint={{ "fill-color": theme.palette.common.black }}
        />
        <Layer
          id={PivotLayerIds.LEG_OUTLINE}
          beforeId={PivotLayerIds.LEG}
          type="line"
          paint={{ "line-color": theme.palette.common.black, "line-width": 2 }}
        />
      </Source>
      <Source type="geojson" data={selectedLineFeatures}>
        <Layer
          id={PivotLayerIds.LEG_SELECT}
          beforeId={beforeId}
          type="fill"
          paint={{
            "fill-color": theme.palette.common.white,
            "fill-opacity": 0.7,
          }}
        />
      </Source>
      <Source type="geojson" data={focusedLineFeatures}>
        <Layer
          id={PivotLayerIds.LEG_FOCUS}
          beforeId={beforeId}
          type="fill"
          paint={{
            "fill-color": theme.palette.common.white,
            "fill-opacity": 0.9,
          }}
        />
      </Source>
    </>
  );
};

export const MultiPivotLayers = React.memo(_MultiPivotLayers);

export const useMultiPivotLayers = ({
  clickPivot,
  hoverPivot,
}: {
  clickPivot?: (feature?: CenterPivotFeature) => void;
  hoverPivot?: (feature?: CenterPivotFeature) => void;
}): LayerConfig<MultiPivotLayersProps> => {
  const interactionLayers: InteractionLayer[] = [
    {
      id: PivotLayerIds.CENTER,
      cursor: "pointer",
      onClick: (e) => clickPivot?.(getCenterPivotFeature(e)),
      onMouseMove: (e) => hoverPivot?.(getCenterPivotFeature(e)),
      onLeave: () => hoverPivot?.(undefined),
    },
    {
      id: PivotLayerIds.LEG,
      cursor: "pointer",
      onClick: (e) => clickPivot?.(getCenterPivotFeature(e)),
      onMouseMove: (e) => hoverPivot?.(getCenterPivotFeature(e)),
      onLeave: () => hoverPivot?.(undefined),
    },
    {
      id: PivotLayerIds.CENTER,
      cursor: "pointer",
      onClick: (e) => clickPivot?.(getCenterPivotFeature(e)),
      onMouseMove: (e) => hoverPivot?.(getCenterPivotFeature(e)),
      onLeave: () => hoverPivot?.(undefined),
    },
  ];
  return {
    interactionLayers,
    Layers: MultiPivotLayers,
    idsToLoad: [],
  };
};
